import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { apiFetch } from '../services/api';

interface UserProfile {
    username: string,
    groups: string[],
    attributes: any
}


interface AuthTokens {
    idToken: string;
    refreshToken: string;
    expireIn: number;
    userProfile: UserProfile;
}

interface AuthContextType {
    isAuthenticated: boolean;
    login: (tokens: AuthTokens) => void;
    logout: () => void;
    isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const logout = useCallback(() => {
        sessionStorage.clear();
        //localStorage.removeItem('refreshToken');
        setIsAuthenticated(false);
        // The AppRoutes component will redirect to /login
    }, []);

    const login = useCallback((tokens: AuthTokens) => {
        sessionStorage.setItem('idToken', tokens.idToken);
        if (tokens.refreshToken) {
            localStorage.setItem('refreshToken', tokens.refreshToken);
        }
        // Store the ID token for API calls in the current session.
        sessionStorage.setItem('username', tokens.userProfile.username);
        sessionStorage.setItem('groups', JSON.stringify(tokens.userProfile.groups));
        sessionStorage.setItem('email', tokens.userProfile.attributes.email);
        sessionStorage.setItem('firstName', tokens.userProfile.attributes.given_name);
        sessionStorage.setItem('lastName', tokens.userProfile.attributes.family_name);
        sessionStorage.setItem('agencyOri', tokens.userProfile.attributes['custom:agency_ori']);
        setIsAuthenticated(true);
    }, []);

    // We check for a refresh token on initial load.
    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (!refreshToken) {
                    setIsLoading(false);
                    return;
                }

                try {
                    const response = await apiFetch('/api/pub/auth/refresh', {
                        method: 'POST',
                        body: JSON.stringify({ refreshToken: refreshToken }),
                    });

                    if (!response.ok) {
                        throw new Error('Session refresh failed');
                    }

                    const newTokens: AuthTokens = await response.json();
                    login(newTokens);


                } catch (error) {
                    console.error("Session refresh failed on initial load", error);
                    logout();
                } finally {
                    setIsLoading(false);
                }
            } catch (e) {
                console.error("An unexpected error occurred during auth check:", e);
                setIsLoading(false);
            }
        };

        checkAuthStatus();
    }, [login, logout]);

    // Global listener for auth errors
    useEffect(() => {
        const handleAuthError = () => {
            logout();
        };
        window.addEventListener('auth-error', handleAuthError);
        return () => {
            window.removeEventListener('auth-error', handleAuthError);
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
